import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface PdfViewerProps {
  reportId: string;
  reportName: string;
  onClose: () => void;
}

export function PdfViewer({ reportId, reportName, onClose }: PdfViewerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoke: string | null = null;
    api.viewReport(reportId)
      .then(async (data) => {
        const res = await fetch(data.url);
        if (!res.ok) throw new Error('Failed to fetch document');
        const blob = await res.blob();
        revoke = URL.createObjectURL(blob);
        setBlobUrl(revoke);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load document'))
      .finally(() => setLoading(false));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [reportId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Block Ctrl+S, Ctrl+P, Ctrl+C
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's' || e.key === 'p' || e.key === 'c') {
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="pdf-viewer-backdrop"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="pdf-viewer-header">
        <span className="pdf-viewer-title">{reportName}</span>
        <span className="pdf-viewer-notice">
          {t('pdfViewer.viewOnly', 'View Only — downloading and copying are disabled')}
        </span>
        <button
          type="button"
          className="btn-secondary pdf-viewer-close"
          onClick={onClose}
        >
          {t('common.close', 'Close')}
        </button>
      </div>
      <div className="pdf-viewer-content">
        {loading && (
          <div className="pdf-viewer-loading">{t('pdfViewer.loading', 'Loading document...')}</div>
        )}
        {error && (
          <div className="pdf-viewer-error alert alert-error">{error}</div>
        )}
        {blobUrl && (
          <>
            {/* Watermark overlay with user email */}
            <div className="pdf-viewer-watermark" aria-hidden="true">
              {Array.from({ length: 12 }, (_, i) => (
                <span key={i} className="pdf-viewer-watermark-text">
                  {user?.email || 'CONFIDENTIAL'}
                </span>
              ))}
            </div>
            {/* Transparent overlay to block right-click and text selection */}
            <div className="pdf-viewer-overlay" />
            <iframe
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              className="pdf-viewer-iframe"
              title={reportName}
            />
          </>
        )}
      </div>
    </div>
  );
}
